import type React from "react";
import { useState, useRef, useEffect } from "react";
import { ArrowRight, Check, ChevronRight, Star } from "lucide-react";
import { Link } from "react-router-dom";

export interface EnhancedProductFeature {
  title: string;
  description: string;
  icon?: React.ReactNode;
  comingSoon?: boolean;
}

export interface EnhancedProductTemplateProps {
  logoSrc: string | null;
  logoAlt: string;
  title: string;
  slogan?: string;
  description: string;
  features: EnhancedProductFeature[];
  imageSrc: string;
  imageAlt: string;
  primaryCtaText?: string;
  primaryCtaLink?: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  colorScheme?:
    | "blue"
    | "purple"
    | "green"
    | "amber"
    | "rose"
    | "cyan"
    | "indigo";
  layout?: "default" | "reversed" | "centered";
  showBadge?: boolean;
  badgeText?: string;
  showStats?: boolean;
  stats?: { label: string; value: string }[];
  showTestimonial?: boolean;
  testimonial?: {
    quote: string;
    author: string;
    role: string;
    company: string;
  };
  showAnimation?: boolean;
}

// Farbschemata für verschiedene Produktkategorien
const colorSchemes = {
  blue: {
    light: "bg-blue-50",
    medium: "bg-blue-100",
    border: "border-blue-200",
    text: "text-blue-700",
    accent: "bg-blue-500",
    gradient: "from-blue-500 to-blue-600",
    shadow: "shadow-blue-500/20",
  },
  purple: {
    light: "bg-purple-50",
    medium: "bg-purple-100",
    border: "border-purple-200",
    text: "text-purple-700",
    accent: "bg-purple-500",
    gradient: "from-purple-500 to-purple-600",
    shadow: "shadow-purple-500/20",
  },
  green: {
    light: "bg-green-50",
    medium: "bg-green-100",
    border: "border-green-200",
    text: "text-green-700",
    accent: "bg-green-500",
    gradient: "from-green-500 to-green-600",
    shadow: "shadow-green-500/20",
  },
  amber: {
    light: "bg-amber-50",
    medium: "bg-amber-100",
    border: "border-amber-200",
    text: "text-amber-700",
    accent: "bg-amber-500",
    gradient: "from-amber-500 to-amber-600",
    shadow: "shadow-amber-500/20",
  },
  rose: {
    light: "bg-rose-50",
    medium: "bg-rose-100",
    border: "border-rose-200",
    text: "text-rose-700",
    accent: "bg-rose-500",
    gradient: "from-rose-500 to-rose-600",
    shadow: "shadow-rose-500/20",
  },
  cyan: {
    light: "bg-cyan-50",
    medium: "bg-cyan-100",
    border: "border-cyan-200",
    text: "text-cyan-700",
    accent: "bg-cyan-500",
    gradient: "from-cyan-500 to-cyan-600",
    shadow: "shadow-cyan-500/20",
  },
  indigo: {
    light: "bg-indigo-50",
    medium: "bg-indigo-100",
    border: "border-indigo-200",
    text: "text-indigo-700",
    accent: "bg-indigo-500",
    gradient: "from-indigo-500 to-indigo-600",
    shadow: "shadow-indigo-500/20",
  },
};

export default function EnhancedProductTemplate({
  logoSrc,
  logoAlt,
  title,
  slogan = "Innovative solutions for modern challenges",
  description,
  features,
  imageSrc,
  imageAlt,
  primaryCtaText = "Get Started",
  primaryCtaLink = "#",
  secondaryCtaText = "Contact Sales",
  secondaryCtaLink = "/contact",
  colorScheme = "blue",
  layout = "default",
  showBadge = false,
  badgeText = "NEW",
  showStats = false,
  stats = [
    { label: "Customers", value: "1,000+" },
    { label: "Satisfaction", value: "99%" },
    { label: "Uptime", value: "99.9%" },
  ],
  showTestimonial = false,
  showAnimation = true,
}: EnhancedProductTemplateProps) {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const colors = colorSchemes[colorScheme];

  // Animation für Scroll-basierte Effekte
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Bestimme das Layout
  const isReversed = layout === "reversed";
  const isCentered = layout === "centered";

  return (
    <section
      ref={sectionRef}
      className={`py-16 relative overflow-hidden ${
        isInView && showAnimation ? "animate-fade-in" : ""
      }`}
    >
      {/* Hintergrund-Elemente */}
      <div className={`absolute inset-0 ${colors.light} opacity-50`}></div>
      <div
        className={`absolute -top-24 -right-24 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl opacity-20 ${colors.medium}`}
      ></div>
      <div
        className={`absolute -bottom-24 -left-24 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl opacity-20 ${colors.medium}`}
      ></div>

      {/* Dekorative Elemente */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div
          className={`bg-white rounded-xl border ${
            colors.border
          } shadow-xl overflow-hidden ${
            colors.shadow
          } transition-all duration-500 ${
            isInView && showAnimation
              ? "translate-y-0 opacity-100"
              : "translate-y-4 opacity-0"
          }`}
        >
          {isCentered ? (
            // Zentriertes Layout
            <div className="p-8 md:p-12">
              {/* Logo und Badge */}
              <div className="flex justify-center items-center mb-6">
                <div
                  className={`h-16 w-16 flex-shrink-0 rounded-xl ${colors.light} flex items-center justify-center p-3`}
                >
                  <img
                    src={logoSrc || "/placeholder.svg"}
                    alt={logoAlt}
                    width={40}
                    height={40}
                  />
                </div>
                {showBadge && (
                  <div
                    className={`ml-4 px-3 py-1 rounded-full text-xs font-bold uppercase bg-gradient-to-r ${colors.gradient} text-white`}
                  >
                    {badgeText}
                  </div>
                )}
              </div>

              {/* Titel und Beschreibung */}
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  {title}
                </h2>
                <p className={`text-lg ${colors.text} font-medium mb-4`}>
                  {slogan}
                </p>
                <p className="text-gray-600 max-w-3xl mx-auto">{description}</p>
              </div>

              {/* Bild */}
              <div className="relative h-[400px] rounded-xl overflow-hidden shadow-lg mb-12 mx-auto max-w-3xl">
                <img
                  src={imageSrc || "/placeholder.svg"}
                  alt={imageAlt}
                  className="object-cover"
                />
                <div
                  className={`absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-60`}
                ></div>
              </div>

              {/* Features */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className={`p-6 rounded-xl ${colors.light} border ${
                      colors.border
                    } transition-all duration-300 hover:shadow-md ${
                      isInView && showAnimation
                        ? "translate-y-0 opacity-100"
                        : "translate-y-4 opacity-0"
                    }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-start mb-4">
                      <div
                        className={`flex-shrink-0 h-8 w-8 rounded-lg ${colors.accent} flex items-center justify-center mr-3`}
                      >
                        {feature.icon || (
                          <Check className="h-5 w-5 text-white" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          {feature.title}
                          {feature.comingSoon && (
                            <span className="ml-2 text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700">
                              Coming soon
                            </span>
                          )}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-600 ml-11">{feature.description}</p>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <Link
                  to={primaryCtaLink}
                  className={`inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r ${colors.gradient} hover:opacity-90 transition-all shadow-lg ${colors.shadow}`}
                >
                  {primaryCtaText} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to={secondaryCtaLink}
                  className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm"
                >
                  {secondaryCtaText}
                </Link>
              </div>

              {/* Stats oder Testimonial */}
              {showStats && (
                <div className="grid grid-cols-3 gap-4 border-t border-gray-200 pt-8">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className={`text-2xl font-bold ${colors.text}`}>
                        {stat.value}
                      </div>
                      <div className="text-sm text-gray-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Standard oder umgekehrtes Layout
            <div
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                isReversed ? "lg:flex-row-reverse" : ""
              } p-8 md:p-12`}
            >
              {/* Text Content */}
              <div
                className={`space-y-8 ${
                  isInView && showAnimation
                    ? "translate-x-0 opacity-100"
                    : `${
                        isReversed ? "translate-x-8" : "-translate-x-8"
                      } opacity-0`
                } transition-all duration-700`}
              >
                {/* Logo und Badge */}
                <div className="flex items-center">
                  {logoSrc !== null && (
                    <div
                      className={`h-16 w-16 flex-shrink-0 mr-4 rounded-xl ${colors.light} flex items-center justify-center p-3`}
                    >
                      <img
                        src={logoSrc || "/placeholder.svg"}
                        alt={logoAlt}
                        width={40}
                        height={40}
                      />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {logoAlt}
                    </h3>
                    {showBadge && (
                      <div
                        className={`mt-1 inline-block px-3 py-1 rounded-full text-xs font-bold uppercase bg-gradient-to-r ${colors.gradient} text-white`}
                      >
                        {badgeText}
                      </div>
                    )}
                  </div>
                </div>

                {/* Titel und Beschreibung */}
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                    {title}
                  </h2>
                  <p className={`text-lg ${colors.text} font-medium mb-4`}>
                    {slogan}
                  </p>
                  <p className="text-gray-600">{description}</p>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4">
                  <Link
                    to={primaryCtaLink}
                    className={`inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r ${colors.gradient} hover:opacity-90 transition-all shadow-lg ${colors.shadow}`}
                  >
                    {primaryCtaText} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                  <Link
                    to={secondaryCtaLink}
                    className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    {secondaryCtaText}
                  </Link>
                </div>

                {/* Features */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    Features
                    <div
                      className={`ml-2 h-1 w-12 ${colors.accent} rounded-full`}
                    ></div>
                  </h3>
                  <ul className="space-y-4">
                    {features.map((feature, index) => (
                      <li
                        key={index}
                        className={`flex items-start transition-all duration-300 ${
                          isInView && showAnimation
                            ? "translate-x-0 opacity-100"
                            : "translate-x-4 opacity-0"
                        }`}
                        style={{ transitionDelay: `${index * 100}ms` }}
                      >
                        <div
                          className={`flex-shrink-0 h-6 w-6 rounded-full ${colors.accent} flex items-center justify-center mr-3 mt-1`}
                        >
                          {feature.icon || (
                            <Check className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 flex items-center">
                            {feature.title}
                            {feature.comingSoon && (
                              <span className="ml-2 text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700">
                                Coming soon
                              </span>
                            )}
                          </span>
                          <span className="text-gray-600 block mt-1">
                            {feature.description}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Stats oder Testimonial */}
                {showStats && (
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                    {stats.map((stat, index) => (
                      <div key={index}>
                        <div className={`text-2xl font-bold ${colors.text}`}>
                          {stat.value}
                        </div>
                        <div className="text-sm text-gray-500">
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Image */}
              <div
                className={`relative ${
                  isInView && showAnimation
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0"
                } transition-all duration-700 ${
                  isReversed ? "order-first lg:order-last" : ""
                }`}
              >
                <div className="relative">
                  {/* Dekorative Elemente um das Bild */}
                  <div
                    className={`absolute -inset-4 ${
                      colors.light
                    } rounded-xl opacity-70 ${
                      showAnimation ? "animate-pulse-slow" : ""
                    }`}
                  ></div>

                  {/* Floating Dots */}
                  {showAnimation && (
                    <>
                      <div
                        className={`absolute -top-6 -right-6 h-12 w-12 rounded-full ${colors.medium} opacity-60 animate-float-slow`}
                      ></div>
                      <div
                        className={`absolute -bottom-8 -left-4 h-16 w-16 rounded-full ${colors.medium} opacity-60 animate-float-slow-reverse`}
                      ></div>
                    </>
                  )}

                  <div className="relative h-[500px] rounded-xl overflow-hidden shadow-xl">
                    <img
                      src={imageSrc || "/placeholder.svg"}
                      alt={imageAlt}
                      className="object-cover"
                    />
                    <div
                      className={`absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-60`}
                    ></div>

                    {/* Floating Feature Highlights */}
                    {showAnimation && features.length > 0 && (
                      <div
                        className={`absolute bottom-4 right-4 p-3 rounded-lg bg-white/90 backdrop-blur-sm shadow-lg max-w-[200px] animate-float-slow`}
                      >
                        <div className="flex items-start">
                          <div
                            className={`flex-shrink-0 h-6 w-6 rounded-full ${colors.accent} flex items-center justify-center mr-2`}
                          >
                            <Star className="h-3 w-3 text-white" />
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">
                              {features[0].title}
                            </h4>
                            <p className="text-xs text-gray-600 line-clamp-2">
                              {features[0].description}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* "Erfahre mehr" Button */}
                  <div className="absolute -bottom-4 -right-4 transform rotate-2">
                    <Link
                      to={primaryCtaLink}
                      className={`inline-flex items-center px-4 py-2 rounded-lg bg-white shadow-lg text-sm font-medium ${colors.text} hover:shadow-xl transition-all`}
                    >
                      Mehr erfahren <ChevronRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
